<template>
  <div class="container" v-if="!isCampaignPending && !isSpacePending">
    <CampaignHeader
      :campaignName="campaign.campaign.internal_name"
      :spaceName="space.name"
      :buttons="[
        {
          class: 'outline secondary icon icon-left',
          label: $t('app.campaign.single.parameters'),
          icon: 'bi-gear',
          disabled: campaign.archived || !canWriteCampaignAndSpaces,
          click: () => {
            appStore.currentCampaignTab = 'Parameters';
            appStore.campaignParametersTab = 1;
            router.push({
              name: 'campaign',
              params: {
                campaignId: route.params.campaignId || '8d9740ed-56ac-4f1f-856f-df9d81f16c52',
              },
            });
          },
        },
        {
          class: 'primary icon icon-left',
          label: $t('app.campaign.single.backToDashboard'),
          icon: 'bi-arrow-left',
          disabled: campaign.archived,
          routeParams: {
            name: 'campaign',
          },
        },
      ]"
    >
    </CampaignHeader>
    <div class="share-card card" data-step="8.2">
      <div class="inline" @click="showLinkData">
        <h4>{{ $t("app.campaign.single.inviteByLink") }}</h4>
        <div class="toggle-container">
          <div class="toggle" @click="showCampaignLinkModal">
            <input type="checkbox" :checked="state.linkActivated" />
            <label />
          </div>
        </div>
        <i class="bi bi-chevron-down" :class="{ open: state.showLink }" v-if="publicInvitation?.id" />
      </div>
      <div class="link-text">
        {{ $t("app.campaign.single.linkDescription") }}
      </div>
      <transition name="fade">
        <div v-if="state.linkActivated && publicInvitation?.id && state.showLink">
          <InputCopy class="link-title" :label="$t('app.campaign.single.campaignLink')" :value="campaignShareLink" />
          <div data-step="8.3">
            <h4 class="link-title">{{ $t("app.campaign.single.activationTitle") }}</h4>
            <div class="link-text">
              {{ $t("app.campaign.single.activationDescription") }}
            </div>
            <div class="radio-group">
              <label :class="['radio-option', 'radio-option-left', state.autoActivateCandidate ? 'radio-option-selected' : '']">
                <input class="auto-radio-button-dot" type="radio" :checked="state.autoActivateCandidate" @change="handleRadioChange(true)" />
                <div>
                  <div class="radio-option-title">{{ $t("app.campaign.single.autoActivate") }}</div>
                  <div class="radio-option-text">
                    {{ $t("app.campaign.single.autoActivateDesc") }}
                  </div>
                </div>
              </label>
              <label :class="['radio-option', !state.autoActivateCandidate ? 'radio-option-selected' : '']">
                <input class="auto-radio-button-dot" type="radio" :checked="!state.autoActivateCandidate" @change="handleRadioChange(false)" />
                <div>
                  <div class="radio-option-title">{{ $t("app.campaign.single.manualActivate") }}</div>
                  <div class="radio-option-text">
                    {{ $t("app.campaign.single.manualActivateDesc") }}
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>
      </transition>
    </div>
    <div class="divider">
      <div class="divider-line" />
      <div>{{ $t("app.campaign.single.or") }}</div>
      <div class="divider-line" />
    </div>
    <div class="form-card card" data-step="8.4">
      <h4>{{ $t("app.campaign.single.inviteByEmail") }}</h4>
      <VForm
        :button="{
          label: $t('app.campaign.single.addTalent'),
          class: 'primary',
          hotDisabled: true,
        }"
        :externalRule="state.email !== '' && state.firstname !== '' && state.lastname !== ''"
        :displayErrors="false"
        :inline="true"
        :callback="addParticipantToList"
        :clean="true"
      >
        <template #form-body="{ errors }">
          <VField
            :label="$t('app.campaign.single.talentEmail')"
            name="add-candidate-email"
            :placeholder="$t('app.campaign.single.emailPlaceholder')"
            type="email"
            v-model="state.email"
            :class="{
              invalid: Object.keys(errors).includes('add-candidate-email'),
            }"
            :rules="{
              email: true,
              not_included: state.newParticipants.map((elt) => elt.email),
            }"
            :displayError="true"
          />
          <VField
            :label="$t('app.campaign.single.talentFirstName') + '*'"
            placeholder="John"
            name="add-candidate-firstname"
            v-model="state.firstname"
            rules="textOnly"
            :class="{
              invalid: Object.keys(errors).includes('add-candidate-firstname'),
            }"
            :displayError="true"
          />
          <VField
            :label="$t('app.campaign.single.talentLastName') + '*'"
            name="add-candidate-lastname"
            placeholder="Doe"
            v-model="state.lastname"
            rules="textOnly"
            :class="{
              invalid: Object.keys(errors).includes('add-candidate-lastname'),
            }"
            :displayError="true"
          />
        </template>
      </VForm>
    </div>
    <div class="card">
      <h4>
        {{ $t("app.campaign.single.importTalentsCSV") }}
        <span>
          <InfoIcon style="width: 16px; margin-bottom: -1; margin-left: 6px;" v-tooltip="$t('app.campaign.single.importTalentsInfo')"></InfoIcon>
        </span>
      </h4>
      <Dragzone @fileSelected="onFileUpload" @fileRemoved="onFileRemoved" />
    </div>
    <div class="data-card card">
      <h4>{{ $t("app.campaign.single.addedTalentsList") }}</h4>
      <p>
        {{ $t("app.campaign.single.addedTalentsDesc") }}
      </p>
      <Table :fields="state.fields" :data="state.newParticipants" :itemsPerPage="100">
        <template #first_name="{ data }">
          <p v-if="!data.first_name" class="muted">{{ $t("app.campaign.single.notProvided") }}</p>
          <template v-else>{{ data.first_name }}</template>
        </template>
        <template #last_name="{ data }">
          <p v-if="!data.last_name" class="muted">{{ $t("app.campaign.single.notProvided") }}</p>
          <template v-else>{{ data.last_name }}</template>
        </template>
        <template #actions="actionsProps">
          <Button :label="$t('app.campaign.single.removeTalent')" class="red outline icon icon-left" icon="bi-trash" @click="removeFromList(actionsProps.row.email)" />
        </template>
        <template #empty>
          <Empty :message="$t('app.campaign.single.noTalentInList', { strong: '<b>' })" :dark="isDark" icon="loop" />
        </template>
      </Table>
      <Button class="primary" :label="$t('app.campaign.single.finishAndBack')" :disabled="state.newParticipants.length < 1" :loading="state.isLoading" @click="addParticipantsToCampaign" />
    </div>
    <ActionModal modal="activate" :data="state.participants" :isOpen="state.showActivateModal" @close="cancelActivateModal" @confirm="cancelActivateModal" />
    <ActionModal modal="deactivateLink" :data="[]" :isOpen="state.showLinkModal" @close="state.showLinkModal = false" @confirm="enableCampaignLink" />
  </div>
</template>

<script setup lang="ts">
import { reactive } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useDark } from "@vueuse/core";
import { useToast } from "vue-toastification";
import * as Xlsx from "xlsx";
import Papa from "papaparse";

import CampaignHeader from "@/components/campaigns/Header.vue";
import Empty from "@/components/table/Empty.vue";
import ActionModal from "@/components/modals/app/actionModal/ActionModal.vue";
import Dragzone from "@/components/global/Dragzone.vue";

import { useAppStore } from "@/stores/app.js";

import { Participant } from "@/types/participant";

import { useActivateParticipations, useParticipations } from "@/queries/participant.query";
import { useCampaign, useEditCampaign, useTogglePublicInvitation } from "@/queries/campaigns.query";
import { useOrganisation, useOrganisationCredits } from "@/queries/organisation.query";
import { useSpace } from "@/queries/space.query";
import { useCampaignPublicInvitation, useDisablePublicInvitation, useEnablePublicInvitation, useInviteInCampaign } from "@/queries/invitation.query";
import { usePermissions } from "@/composables/permissions";

import InfoIcon from '@/assets/images/icon/campaign/result_panel/info.svg?component';
import { invitationApi } from "@/api/invitation.api";


const isDark = useDark();
const route = useRoute();
const router = useRouter();
const toast = useToast();
const { t } = useI18n();

// Init stores
const appStore = useAppStore();

const { canWriteCampaignAndSpaces } = usePermissions();
const { data: space, isPending: isSpacePending } = useSpace();
const { data: credits, isPending: isCreditsPending } = useOrganisationCredits();
const { data: campaign, isPending: isCampaignPending} = useCampaign(appStore.onboardingIsLive);
const { data: publicInvitation, isPending: isPublicInvitationPending } = useCampaignPublicInvitation();
const { mutate: editCampaign } = useEditCampaign();

const { data: participations, isPending } = useParticipations(appStore.onboardingIsLive);
const { mutateAsync: activateParticipations } = useActivateParticipations();
const { mutateAsync: inviteInCampaign } = useInviteInCampaign();
const { mutate: enablePublicInvitation } = useEnablePublicInvitation();
const { mutate: disablePublicInvitation } = useDisablePublicInvitation();

interface CandidateRow {
  email: string,
  first_name: string,
  last_name: string,
}

const emailMapping = [
  "email",
  "courriel",
  "mail",
  "e-mail",
  'mél',
];

const firstNameMappping = [
  "first name",
  "prénom",
  "firstname",
  "first_name"
];

const lastNameMappping = [
  "last name",
  "nom",
  "lastname",
  "last_name"
];

const campaignShareLink = computed(() => {
  if (isCampaignPending.value || !campaign.value || !publicInvitation?.value?.id)
    return null;

  return `${window.location.host.replace("app", "game")}/join?invitation_id=${
    publicInvitation.value.id
  }`;
});

const state = reactive({
  email: "",
  firstname: "",
  lastname: "",
  activateModalIsLoading: false,
  isLoading: false,
  showLink: false,
  autoActivateCandidate: campaign.value?.auto_activate,
  linkActivated: false,
  showParameters: false,
  showActivateModal: false,
  showLinkModal: false,
  newParticipants: [],
  participants: [],
  fields: [
    {
      id: "first_name",
      label: t("app.form.register.firstName"),
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "last_name",
      label: t("app.form.register.lastName"),
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "email",
      label: t("app.form.email"),
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
  ],
});

function onFileUpload(file: File) {
  const fileExtension = file.name.split('.').pop().toLowerCase();

  if (fileExtension === 'csv') {
    parseCsvAndMap(file, addCandidatesToList);
  } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
    parseExcelAndMap(file, addCandidatesToList);
  } else {
    toast.error('Unsupported file format');
  }
}

function onFileRemoved(file: File) {
  const fileExtension = file.name.split('.').pop().toLowerCase();

  if (fileExtension === 'csv') {
    parseCsvAndMap(file, removeCandidatesFromList);
  } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
    parseExcelAndMap(file, removeCandidatesFromList);
  } else {
    toast.error('Unsupported file format');
  }
}

function addCandidatesToList(candidates: CandidateRow[]) {
  for (const row of candidates) {
    if (state.newParticipants.some((elt) => elt.email === row.email)) {
      continue;
    }

    state.newParticipants.push({
      email: row.email,
      first_name: row.first_name,
      last_name: row.last_name,
      activeAssessment: true,
    });
  }
}

function removeCandidatesFromList(candidates: CandidateRow[]) {
  for (const row of candidates) {
    state.newParticipants = state.newParticipants.filter(elt => elt.email !== row.email);
  }
}

function parseCsvAndMap(file: File, callback: (candidates: CandidateRow[]) => void) {
  Papa.parse(file, {
    header: true,       // convert rows to objects
    skipEmptyLines: true,
    transformHeader: (header: string) => { // normalize header names
      const trimmedHeader = header.toLowerCase().trim();
      if (emailMapping.includes(trimmedHeader)) {
        return "email";
      } else if (firstNameMappping.includes(trimmedHeader)) {
        return "first_name";
      } else if (lastNameMappping.includes(trimmedHeader)) {
        return "last_name";
      }
      return trimmedHeader;
    },
    complete: (results) => {
      if (results.data[0]["email"] === undefined || results.data[0]["first_name"] === undefined || results.data[0]["last_name"] === undefined) {
        toast.error(t('app.campaign.single.importTalentsInfo'));
        return
      }
      callback(results.data);
    },
    error: (err) => {
      console.error('CSV Error:', err);
    }
  })
}

async function parseExcelAndMap(file: File, callback: (candidates: CandidateRow[]) => void) {
    const data = await file.arrayBuffer();
    const workbook = Xlsx.read(data, { type: 'array' });

    // Get first sheet
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];

    // Convert to JSON
    const rows: any[] = Xlsx.utils.sheet_to_json(worksheet, {
      defval: null,
      header: 1,
    });

    if (!rows.length) return;
    const originalHeaders: string[] = rows[0];
    const transformedHeaders = originalHeaders.map((header: string) => { // normalize header names
      const trimmedHeader = header.toLowerCase().trim();
      if (emailMapping.includes(trimmedHeader)) {
        return "email";
      } else if (firstNameMappping.includes(trimmedHeader)) {
        return "first_name";
      } else if (lastNameMappping.includes(trimmedHeader)) {
        return "last_name";
      }
      return trimmedHeader;
    });
    const candidates: CandidateRow[] = rows.slice(1).map(row => {
      let obj: CandidateRow = {email: "", first_name: "", last_name: ""};
      transformedHeaders.forEach((header, i) => {
        obj[header] = row[i];
      });
      return obj;
    });
    callback(candidates);
}

function addParticipantToList() {
  state.newParticipants.unshift({
    email: state.email,
    first_name: state.firstname,
    last_name: state.lastname,
    activeAssessment: true,
  });
}

function removeFromList(email) {
  const entry = state.newParticipants.findIndex((elt) => elt.email === email);

  state.newParticipants.splice(entry, 1);
}

async function activateAllParticipants(allParticipants: Participant[]) {
  const participantIds = allParticipants.map((elt) => elt.participant_id);

  if (participantIds.length > 0) {
    // avoid error if participants already added
    await activateParticipations({
      context: {
        organisationId: route.params.companyId as string,
        spaceId: route.params.spaceId as string,
        campaignId: route.params.campaignId as string,
      },
      participantIds,
    });

  }
}

async function addParticipantsToCampaign() {
  state.isLoading = true;

  // If not enough credits do not create participants
  // Just display warning message
  if (state.newParticipants.length > (credits.value?.available ?? 0)) {
    state.participants = state.newParticipants;
    state.showActivateModal = true;
    state.isLoading = false;
    return;
  }

  // Remove duplicates
  state.newParticipants = state.newParticipants.filter((elt) => !participations.value.some((participant) => (participant?.candidate?.email === elt.email || participant?.pending_email === elt.email)));
  if (state.newParticipants.length === 0) return;

  const promises: Promise<void>[] = [];
  state.newParticipants.forEach((participant) => {
    const p = inviteInCampaign({
      campaignId: route.params.campaignId as string,
      email: (participant.email as string).toLocaleLowerCase(),
      firstName: participant.first_name,
      lastName: participant.last_name,
    });
    promises.push(p);
  });

  try {
    await Promise.all(promises);
    toast.success(t("app.campaign.single.talentsAdded", { count: promises.length }));
    // await activateAllParticipants(data.participants);
    goBack();
  } catch (error) {
    console.error(error);
  } finally {
    appStore.activatedTableHasNews[`${route.params.campaignId}`] = true;
    state.isLoading = false;
  }
}

function cancelActivateModal() {
  state.showActivateModal = false;
}

function goBack() {
  appStore.currentCampaignTab = "Overview";

  state.newParticipants = [];

  router.push({ name: "campaign" });
}

function showLinkData() {
  if (state.showLink) {
    state.showLink = false;
  } else if (publicInvitation?.value?.id && state.linkActivated) {
    state.showLink = true;
  }
}

function showCampaignLinkModal(event) {
  if (state.linkActivated) {
    event.preventDefault();
    state.showLinkModal = true;
  } else {
    enableCampaignLink();
  }
}

async function enableCampaignLink() {
  state.linkActivated = !state.linkActivated;
  state.isLoading = true;
  if (!state.linkActivated && publicInvitation?.value?.id) {
    disablePublicInvitation({ invitationId: publicInvitation?.value?.id });
  } else if (state.linkActivated && !publicInvitation?.value?.id) {
    enablePublicInvitation({ campaignId: route.params.campaignId as string });
  }
  state.isLoading = false;
  state.showLink = state.linkActivated;
  console.log(state.showLink);

  state.showLinkModal = false;
}

async function handleRadioChange(value) {
  state.autoActivateCandidate = value;

  editCampaign({
    context: {
      organisationId: route.params.companyId as string,
      spaceId: route.params.spaceId as string,
      campaignId: campaign.value.id,
    },
    campaign: campaign.value,
    auto_activate: state.autoActivateCandidate,
  });
}

watch(publicInvitation, () => {
  state.linkActivated = !!publicInvitation?.value?.id;
  state.autoActivateCandidate = !publicInvitation?.value?.requires_approval;
});

onMounted(() => {
  state.linkActivated = !!publicInvitation?.value?.id;
  state.autoActivateCandidate = !publicInvitation?.value?.requires_approval;
});
</script>
